import { css as _css } from "@emotion/core";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@domain-group/fe-co-allhomes-design-system';
import Loader from '@domain-group/fe-co-loader';
import { CloseIconSmall, SearchIconSmall } from '@domain-group/fe-co-icon';
import SelectedItemList from '../components/selected-items';
import Messages from '../components/messages';
import Suggestions from '../components/suggestions';
import RecentItems from '../components/recent-items';
import PlaceholderIcon from './placeholder-icon';
import { MIN_QUERY_LENGTH } from '../utils/constants';
import useAutocomplete from '../utils/use-autocomplete';
import * as autocompletePropTypes from '../utils/prop-types';
import * as styles from './autocomplete-dropdown.style';
import SuggestionHeading from '../components/suggestion-heading';
import { jsx as ___EmotionJSX } from "@emotion/core";
var AutocompleteDropdown = _ref => {
  var setQueryText = _ref.setQueryText,
    queryText = _ref.queryText,
    suggestions = _ref.suggestions,
    initialSelectedItems = _ref.initialSelectedItems,
    types = _ref.types,
    immediateTypes = _ref.immediateTypes,
    buildGroupName = _ref.buildGroupName,
    placeholder = _ref.placeholder,
    inputType = _ref.inputType,
    singleSelect = _ref.singleSelect,
    showEmptySheet = _ref.showEmptySheet,
    noBorder = _ref.noBorder,
    buttonText = _ref.buttonText,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'normal' : _ref$size,
    loading = _ref.loading,
    pending = _ref.pending,
    invalid = _ref.invalid,
    notFoundText = _ref.notFoundText,
    error = _ref.error,
    className = _ref.className,
    recentSearchKey = _ref.recentSearchKey,
    saveRecentOnSearch = _ref.saveRecentOnSearch,
    onLoadedRecentSearches = _ref.onLoadedRecentSearches,
    onImmediateSearch = _ref.onImmediateSearch,
    onSearch = _ref.onSearch,
    onRecentSearch = _ref.onRecentSearch,
    onSelectionChange = _ref.onSelectionChange,
    onEntryClick = _ref.onEntryClick,
    extensions = _ref.extensions,
    onDropdownOpen = _ref.onDropdownOpen;
  var _useAutocomplete = useAutocomplete({
      setQueryText,
      suggestions,
      initialSelectedItems,
      types,
      singleSelect,
      immediateTypes,
      queryText,
      onSearch,
      onImmediateSearch,
      onRecentSearch,
      onSelectionChange,
      recentSearchKey,
      saveRecentOnSearch,
      onLoadedRecentSearches
    }),
    isOpen = _useAutocomplete.isOpen,
    filteredSuggestions = _useAutocomplete.filteredSuggestions,
    hasData = _useAutocomplete.hasData,
    selectedItems = _useAutocomplete.selectedItems,
    recentItems = _useAutocomplete.recentItems,
    highlightedIndex = _useAutocomplete.highlightedIndex,
    removeSelectedItem = _useAutocomplete.removeSelectedItem,
    openMenu = _useAutocomplete.openMenu,
    closeMenu = _useAutocomplete.closeMenu,
    onSearchButtonClick = _useAutocomplete.onSearchButtonClick,
    getComboboxProps = _useAutocomplete.getComboboxProps,
    getSelectedItemProps = _useAutocomplete.getSelectedItemProps,
    setInputValue = _useAutocomplete.setInputValue,
    getInputProps = _useAutocomplete.getInputProps,
    getMenuProps = _useAutocomplete.getMenuProps,
    getItemProps = _useAutocomplete.getItemProps;
  var inputRef = useRef();
  useEffect(() => {
    if (isOpen) {
      inputRef.current.focus();
    }
    onDropdownOpen(isOpen);
  }, [isOpen, onDropdownOpen]);
  var openAndFocus = () => {
    if (pending) {
      return;
    }
    if (!isOpen) {
      openMenu();
      onEntryClick();
    }
    if (isOpen) {
      inputRef.current.focus();
    }
  };
  var handleSearchButtonClick = event => {
    event.stopPropagation();
    if (singleSelect) {
      openAndFocus();
    } else {
      onSearchButtonClick();
    }
  };
  var handleClearButtonClick = event => {
    event.stopPropagation();
    setInputValue('');
    setQueryText('');
    closeMenu();
  };
  var behaviour = isOpen ? 'newline' : 'truncate';
  var showSheet = isOpen && (hasData || showEmptySheet || queryText.length >= MIN_QUERY_LENGTH || error || recentItems.length > 0);
  var showSearchButton = inputType === 'standalone' && (!singleSelect || !isOpen && queryText.length === 0);
  var showCloseButton = singleSelect && (queryText.length > 0 || isOpen);
  return ___EmotionJSX("div", {
    css: styles.spaceReserver,
    className: className
  }, ___EmotionJSX("div", {
    css: /*#__PURE__*/_css([styles.autocomplete, pending && styles.disabled])
  }, ___EmotionJSX("div", {
    css: styles.inputWrapper({
      size,
      noBorder,
      invalid,
      isOpen
    }),
    onClick: openAndFocus
  }, inputType === 'form' && ___EmotionJSX(PlaceholderIcon, {
    css: styles.placeholderIcon({
      size,
      isOpen
    })
  }), ___EmotionJSX("div", _extends({
    css: styles.itemList({
      size,
      isOpen,
      singleSelect
    })
  }, getComboboxProps()), selectedItems.length > 0 && (!singleSelect || !isOpen) && ___EmotionJSX(SelectedItemList, {
    css: styles.selectedItems,
    isOpen: isOpen,
    singleSelect: singleSelect,
    behaviour: behaviour,
    selectedItems: selectedItems,
    getSelectedItemProps: getSelectedItemProps,
    removeSelectedItem: removeSelectedItem
  }), ___EmotionJSX("input", _extends({
    disabled: pending,
    key: "input",
    placeholder: !singleSelect && selectedItems.length > 0 ? '' : placeholder,
    css: /*#__PURE__*/_css([styles.comboxInput({
      isOpen: isOpen || selectedItems.length === 0
    }), pending && styles.disabled])
  }, getInputProps({
    ref: inputRef,
    onFocus: openAndFocus
  }), {
    enterKeyHint: "search"
  })), showCloseButton && ___EmotionJSX(Button, {
    css: styles.closeButton,
    size: "small",
    appearance: "ghostNeutral",
    onClick: handleClearButtonClick,
    icon: ___EmotionJSX(CloseIconSmall, null)
  })), showSearchButton && ___EmotionJSX(Button, {
    css: styles.searchButton({
      size,
      noBorder,
      isOpen,
      buttonText
    }),
    size: "small",
    appearance: "accent",
    icon: pending ? ___EmotionJSX(Loader, {
      transparent: true,
      css: styles.spinner,
      size: "mini"
    }) : ___EmotionJSX(SearchIconSmall, {
      size: "small"
    }),
    onClick: handleSearchButtonClick
  }, buttonText)), ___EmotionJSX("div", {
    css: styles.optionSheetContainer
  }, ___EmotionJSX("ul", _extends({
    css: styles.optionSheet({
      isOpen: showSheet
    })
  }, getMenuProps()), isOpen && ___EmotionJSX(React.Fragment, null, recentItems.length ? ___EmotionJSX("li", null, ___EmotionJSX(SuggestionHeading, null, "Recent Searches"), ___EmotionJSX(RecentItems, {
    highlightedIndex: highlightedIndex,
    getItemProps: getItemProps,
    recentItems: recentItems
  })) : ___EmotionJSX("li", null, extensions !== null && extensions !== void 0 && extensions.suggestionHeading ? ___EmotionJSX("div", {
    css: styles.suggestionExtension
  }, extensions.suggestionHeading) : ___EmotionJSX(SuggestionHeading, null, "Suggestions"), ___EmotionJSX(Suggestions, {
    queryText: queryText,
    getItemProps: getItemProps,
    indexOffset: recentItems.length,
    highlightedIndex: highlightedIndex,
    suggestions: filteredSuggestions,
    buildGroupName: buildGroupName,
    immediateTypes: immediateTypes,
    singleSelect: singleSelect
  }))), isOpen && !hasData && ___EmotionJSX("div", _extends({
    css: styles.message
  }, getMenuProps()), ___EmotionJSX(Messages, {
    loading: loading,
    error: error,
    queryText: queryText,
    notFoundText: notFoundText
  }))))));
};
AutocompleteDropdown.propTypes = {
  suggestions: autocompletePropTypes.valueList,
  recentItems: autocompletePropTypes.valueList,
  initialSelectedItems: autocompletePropTypes.valueList,
  size: autocompletePropTypes.size,
  types: autocompletePropTypes.types,
  inputType: autocompletePropTypes.inputType,
  singleSelect: PropTypes.bool,
  showEmptySheet: PropTypes.bool,
  noBorder: PropTypes.bool,
  immediateTypes: autocompletePropTypes.types,
  buildGroupName: autocompletePropTypes.buildGroupName,
  extensions: PropTypes.shape({
    suggestionHeading: PropTypes.node
  }),
  placeholder: PropTypes.string,
  buttonText: PropTypes.string,
  mobilePlaceholder: PropTypes.string,
  className: PropTypes.string,
  recentSearchKey: PropTypes.string,
  saveRecentOnSearch: PropTypes.bool,
  loading: PropTypes.bool,
  pending: PropTypes.bool,
  invalid: PropTypes.bool,
  error: PropTypes.bool,
  notFoundText: PropTypes.string,
  queryText: PropTypes.string,
  setQueryText: PropTypes.func,
  onSearch: PropTypes.func,
  onImmediateSearch: PropTypes.func,
  onRecentSearch: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onLoadedRecentSearches: PropTypes.func,
  onEntryClick: PropTypes.func,
  onDropdownOpen: PropTypes.func
};
AutocompleteDropdown.defaultProps = {
  suggestions: [],
  recentItems: [],
  initialSelectedItems: [],
  size: 'normal',
  placeholder: '',
  buttonText: '',
  types: [],
  singleSelect: false,
  showEmptySheet: false,
  noBorder: false,
  immediateTypes: [],
  buildGroupName: undefined,
  extensions: undefined,
  inputType: 'standalone',
  mobilePlaceholder: undefined,
  className: undefined,
  recentSearchKey: undefined,
  saveRecentOnSearch: undefined,
  loading: false,
  pending: false,
  invalid: false,
  error: false,
  notFoundText: undefined,
  queryText: '',
  setQueryText: () => {},
  onSearch: () => {},
  onImmediateSearch: undefined,
  onRecentSearch: undefined,
  onSelectionChange: () => {},
  onLoadedRecentSearches: () => {},
  onEntryClick: () => {},
  onDropdownOpen: () => {}
};
export default AutocompleteDropdown;